/**
 * useTurnstile — render a Cloudflare Turnstile widget into a container and
 * expose its token.
 *
 * Extracted from CommentModal's widget handling so the share modal (a second
 * guest-capable write) shares one loader instead of a second copy. The widget
 * renders once the container exists (`ready`), resets on demand, and is removed
 * when the caller unmounts or `active` turns off.
 */

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { TURNSTILE_CONFIG } from '../config/integrations';

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
}

function turnstileApi(): TurnstileApi | undefined {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile;
}

export function useTurnstile(active: boolean, containerRef: RefObject<HTMLDivElement>, ready: boolean): { token: string; reset: () => void } {
  const [token, setToken] = useState('');
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!active || !ready || !containerRef.current) return;

    const renderWidget = () => {
      const api = turnstileApi();
      if (widgetId.current !== null) { api?.reset(widgetId.current); return; }
      if (api && containerRef.current) {
        widgetId.current = api.render(containerRef.current, {
          sitekey: TURNSTILE_CONFIG.siteKey,
          callback: (t: string) => setToken(t),
          'expired-callback': () => setToken(''),
          theme: 'light',
        });
      }
    };

    if (turnstileApi()) {
      renderWidget();
      return;
    }
    const existing = document.querySelector('script[src*="turnstile"]');
    if (!existing) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => setTimeout(renderWidget, 100);
      document.head.appendChild(script);
      return;
    }
    const checkReady = setInterval(() => {
      if (turnstileApi()) { clearInterval(checkReady); renderWidget(); }
    }, 100);
    return () => clearInterval(checkReady);
  }, [active, ready, containerRef]);

  // Remove the widget when it is no longer wanted (modal closed / step left).
  useEffect(() => {
    if ((active && ready) || widgetId.current === null) return;
    try { turnstileApi()?.remove(widgetId.current); } catch { /* ignore */ }
    widgetId.current = null;
    setToken('');
  }, [active, ready]);

  useEffect(() => () => {
    if (widgetId.current !== null) {
      try { turnstileApi()?.remove(widgetId.current); } catch { /* ignore */ }
      widgetId.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setToken('');
    if (widgetId.current !== null) {
      try { turnstileApi()?.reset(widgetId.current); } catch { /* ignore */ }
    }
  }, []);

  return { token, reset };
}
